import React, { useMemo } from 'react'
import { Text, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import { BtAlert } from '@linqpal/components/src/ui'
import { useResponsive } from '../../../utils/hooks'
import { observer } from 'mobx-react-lite'
import { useNavigation } from '@react-navigation/core'
import projectsStore from '../../Projects/Store'
import InvoiceActionsStore from './InvoiceActionsStore'

export const GroupedInvoicesProjectWarning: React.FC<{
  selectedInvoices: any[]
}> = observer(({ selectedInvoices }) => {
  const { t } = useTranslation('global')
  const navigation = useNavigation()
  const { sm } = useResponsive()
  const { isProjectMissingOnAllInvoices, areInvoicesFromSameProject } =
    projectsStore
  const {
    groupedInvoiceProjectWarning,
    setGroupedInvoiceProjectWarning,
    projectNavigation,
    resumeApplyingForLoan,
  } = InvoiceActionsStore
  const invoiceIds: string = useMemo(() => {
    const ids = selectedInvoices.map((s) => s._id)
    return ids.join(',')
  }, [selectedInvoices])

  const isProjectMissing = useMemo(
    () => isProjectMissingOnAllInvoices(selectedInvoices),
    [selectedInvoices, isProjectMissingOnAllInvoices],
  )

  const onDismiss = () => {
    setGroupedInvoiceProjectWarning(false)
  }

  const onContinue = () => {
    if (isProjectMissing) {
      projectNavigation(navigation, invoiceIds)
    } else {
      resumeApplyingForLoan()
    }
    onDismiss()
  }

  const restrictApplyingForLoanButton = [
    {
      label: 'Go Back',
      appearance: 'outline',
      style: sm && styles.singleButtonDesktop,
      onPress: onDismiss,
    },
  ]

  return (
    <BtAlert
      visible={groupedInvoiceProjectWarning}
      onClose={onDismiss}
      buttons={
        areInvoicesFromSameProject(selectedInvoices) || isProjectMissing
          ? [
              {
                label: 'Go Back',
                appearance: 'outline',
                style: sm && styles.buttonDesktop,
                onPress: onDismiss,
              },
              {
                label: 'Continue',
                style: sm && styles.buttonDesktop,
                onPress: onContinue,
              },
            ]
          : restrictApplyingForLoanButton
      }
      margins={!sm ? 18 : undefined}
      width={sm && 650}
      buttonsContainerStyle={sm && { justifyContent: 'center' }}
      verticalButtons={!sm}
    >
      <Text style={sm ? styles.desktopTitleStyle : styles.mobTitleStyle}>
        {t('GroupedInvoicesProjectWarning.title')}
      </Text>
      <Text style={sm ? styles.desktopBody : styles.mobBody}>
        {t('GroupedInvoicesProjectWarning.subtitle')}
      </Text>
    </BtAlert>
  )
})

const styles = StyleSheet.create({
  desktopTitleStyle: {
    fontWeight: '700',
    fontSize: 28,
    lineHeight: 36,
    color: '#003353',
    marginBottom: 10,
    width: 493,
    textAlign: 'center',
  },
  mobTitleStyle: {
    fontWeight: '600',
    fontSize: 18,
    lineHeight: 28,
    color: '#003353',
    textAlign: 'center',
    marginBottom: 11,
  },
  boldDesktop: {
    fontWeight: '600',
    color: '#003353',
  },
  boldMob: {
    fontWeight: '500',
    color: '#335C75',
    lineHeight: 24,
  },
  desktopBody: {
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 22,
    color: '#003353',
    textAlign: 'center',
    width: 464,
    marginBottom: 30,
  },
  mobBody: {
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 26,
    color: '#668598',
    textAlign: 'center',
  },
  buttonDesktop: {
    width: 210,
    marginBottom: 40,
    marginRight: 5,
    marginLeft: 5,
  },
  singleButtonDesktop: {
    width: 300,
    marginBottom: 40,
    marginRight: 5,
    marginLeft: 5,
  },
})
